import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Loader from "../components/UI/Loader";
import ParticipantProfileHeader from "../components/UI/ParticipantProfileHeader";
import SideMenu from "../components/UI/SideMenu";
import { getClientById } from "../services/clientsService";
import { getParticipantOptions } from "../utils/sideMenuHelpers";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";

const Participant = () => {
    const { id = '' } = useParams();
    const [loadingClient, setLoadingClient] = useState(true);
    const [client, setClient] = useState<any>(null);
    const [clientApiError, setClientApiError] = useState<any>();
    const [headerExpanded, setHeaderExpanded] = useState(true);
    const [participantRefreshVersion, setParticipantRefreshVersion] = useState(0);
    const location = useLocation();

    useEffect(() => {
        if (location?.pathname) {
            setHeaderExpanded(location.pathname.toLowerCase().endsWith("/profile-demographics"));
        }
    }, [location]);

    useEffect(() => {
        document.title = "";
    }, []);

    useEffect(() => {
        if (!id) {
            return;
        }

        setLoadingClient(true);
        setClient(null);
        setClientApiError(undefined);

        getClientById({ id, includePrograms: true }).then((c) => {
            setClient(c);
            const initials = [];
            if (c.firstName && c.firstName.trim().length > 0) {
                initials.push(c.firstName[0].toUpperCase());
            }

            if (c.lastName && c.lastName.trim().length > 0) {
                initials.push(c.lastName[0].toUpperCase());
            }

            const joinedInitials = initials.length > 0 ? `${initials.join('.')}.` : '';

            document.title = `${(joinedInitials.length > 0 ? `${joinedInitials} - ` : '')}Manage Participant`;
        }).catch(e => {
            console.error(e);
            setClientApiError(e);
        }).finally(() => {
            setLoadingClient(false);
        })
    }, [id]);

    const handleOnParticipantUpdated = () => {
        setLoadingClient(true);
        setClient(null);
        setClientApiError(undefined);

        getClientById({ id, includePrograms: true }).then(c => {
            setClient(c);
            setParticipantRefreshVersion(version => version + 1);
        }).catch(e => {
            console.error(e);
            setClientApiError(e);
        }).finally(() => {
            setLoadingClient(false);
        });
    };

    const getHeaderContent = () => {
        if (loadingClient) {
            return <Loader />
        }

        if (!client) {
            return clientApiError
                ? <ApiErrorMessages error={clientApiError} />
                : <>Failed to load</>
        }

        return (
            <div className="hide-on-print">
                <ParticipantProfileHeader
                    client={client}
                    onUpdated={handleOnParticipantUpdated}
                    isExpanded={headerExpanded}
                    onExpandChange={setHeaderExpanded}
                />
            </div>
        );
    };

    return (
        <section id="main-content" tabIndex={-1}>

            <div className="section">
                <SideMenu ariaLabel="Manage Participant Side Menu"
                    id="participant-profile-side-menu"
                    title="Manage Participant"
                    options={getParticipantOptions(id)}
                    headerContent={getHeaderContent()}
                    mainContent={client ? <Outlet context={[client, participantRefreshVersion]} /> : null}
                />
            </div>
        </section>
    );
};

export default Participant;
